<template>
  <teleport to="body">
    <div
      v-show="show"
      data-testid="q-error-cat"
      class="fixed bottom-6 right-6 z-10 w-[35rem] overflow-hidden rounded bg-white shadow-sm"
    >
      <header class="flex h-16 items-center px-6 py-4" :class="isWarning ? 'bg-yellow-60' : 'bg-red-50'">
        <p class="text-lg font-medium text-white">
          {{ title }}
          <strong v-if="status">({{ status }})</strong>
        </p>
        <button class="absolute right-0 top-0 p-4 text-white" type="button" @click="resetError">
          <x-icon class="icon-m" />
        </button>
      </header>

      <div class="p-6">
        <div v-if="debugLog" class="mb-6">
          <p class="text-xs leading-[18px]">{{ $t('modules.application.error_cat.description') }}</p>
          <ul class="list-disc pl-4 text-xs leading-[18px]">
            <li v-if="debugLog">{{ $t('modules.application.error_cat.bullet1') }}</li>
            <li>{{ $t('modules.application.error_cat.bullet2') }}</li>
          </ul>
        </div>
        <p v-if="message" class="mb-6 mt-2 break-words text-xs">{{ message }}</p>
        <footer class="flex flex-wrap items-center gap-2">
          <button
            v-if="debugLog"
            type="button"
            class="bg-blue-20 h-6 rounded-full px-3 text-xs font-medium"
            @click="copyDebugInfo"
          >
            {{ $t('modules.application.error_cat.copy_debug') }}
          </button>
          <button
            v-if="code !== 'access-denied'"
            class="bg-active-primary mr-4 h-6 rounded-full px-3 text-xs font-medium text-white"
            type="button"
            @click="openChat"
          >
            <span>{{ $t('modules.application.navbar.chat') }}</span>
          </button>
          <p class="text-xs text-gray-50">{{ formatDateIntl(date, dateFormatting) }}</p>
        </footer>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { ref } from 'vue'

import { XIcon } from '@klausapp/ui-kit/icons'

import i18n from '@/i18n'
import { useEvent } from '@/composables/useEvent'
import { copy } from '@/utils/clipboard'
import { openSupport } from '@/utils/support'
import analytics from '@/utils/analytics'
import { formatDateIntl } from '@/utils/date'

import type { CatError } from '../types'

const dateFormatting: Intl.DateTimeFormatOptions = {
  timeZone: 'UTC',
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  timeZoneName: 'short',
}

const show = ref(false)
const message = ref('')
const title = ref(i18n.t('modules.application.error_cat.heading'))
const isWarning = ref(false)
const code = ref<string | number>(0)
const status = ref<number | undefined>(undefined)
const date = ref(new Date())
const debugLog = ref('')
let errorTimeout: ReturnType<typeof setTimeout> | undefined

const handleError = async (err: CatError) => {
  show.value = true
  message.value = err.message || ''

  if (err.title) title.value = err.title

  if (err.warning) {
    isWarning.value = true
    return
  }

  if (typeof err.error === 'string') {
    try {
      err = JSON.parse(err.error)
    } catch {
      console.error('Error is not valid JSON. Displaying plain-text error')
    }
  }

  code.value = err.ref || err.code || 0
  status.value = err.status

  const parsedDate = err.dateTime ? Date.parse(err.dateTime) : err.timestamp
  date.value = parsedDate ? new Date(parsedDate) : new Date()

  if (err.ref === 'access-denied') {
    message.value = err.error
  } else if (err.status === 401) {
    message.value = i18n.t('universal.errors.401_v2')
  } else if (err.status === 403) {
    message.value = i18n.t('universal.errors.403')
  } else {
    debugLog.value = await generateDebugLog(err)
  }

  errorTimeout = setTimeout(resetError, 3 * 60 * 1000)
}

const buildDebugObject = async (obj: Request) => {
  let body

  try {
    body = await obj.clone().json()
  } catch {
    body = obj.body
  }

  const debug = {
    url: obj.url,
    method: 'method' in obj ? obj.method : undefined,
    headers: {
      Account: obj.headers.get('X-Klaus-Account'),
      Workspace: obj.headers.get('X-Klaus-Workspace'),
    },
    body,
  }

  return JSON.stringify(debug, null, 2)
}

const generateDebugLog = async ({ ref, request, response, error, message }: CatError) => {
  const log = []
  console.info('req', request)

  if (request) {
    log.push('Request:')
    const formattedReq = await buildDebugObject(request)
    log.push(formattedReq)
  }

  if (response) {
    log.push('Response:')
    log.push(JSON.stringify(response))
  }

  if (ref) {
    log.push('Reference:\n' + ref)
  }

  if (error || message) {
    log.push('Error:\n' + typeof error === 'object' && 'en' in error ? error.en : error || message)
  }

  log.push('Time:')
  log.push(Intl.DateTimeFormat('en-US', dateFormatting).format(date.value))

  return log.join('\n')
}

const copyDebugInfo = () => {
  copy(debugLog.value)
}

const openChat = () => {
  openSupport()
  analytics.navbarEvent('Clicked navbar popup item', 'Chat with us')
}

const resetError = () => {
  show.value = false
  message.value = ''
  code.value = 0
  debugLog.value = ''
  if (errorTimeout) clearTimeout(errorTimeout)
  errorTimeout = undefined
}

useEvent('universal-error', handleError)
</script>
